import { AuthPartner } from "@happy-legal/graphql-types";
import { Box, Typography } from "@mui/material";
import CachedIcon from "@mui/icons-material/CachedOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutlineOutlined";
import EditIcon from "@mui/icons-material/EditOutlined";
import CopyIcon from "@mui/icons-material/ContentCopyOutlined";
import { useContext, useEffect, useState } from "react";
import FormattedDateTimeTooltip from "../../../shared/components/FormattedDateTimeTooltip";
import SettingsSection from "../../../shared/components/SettingsSection";
import {
  ActionButton,
  StatusBadge,
  TableActions,
  TableCell,
  TableRow,
} from "../../../shared/components/table/StyledTable";
import { errorMessage } from "../../../shared/consts";
import { isSure } from "../../../shared/helpers";
import AuthPartnerProvider, {
  AuthPartnerContext,
} from "../../../shared/providers/AuthPartnerProvider";
import {
  DELETE_AUTH_PARTNER,
  GET_ALL_AUTH_PARTNERS,
  RECREATE_AUTH_PARTNER_KEY_PAIR,
  TAuthPartnerWithSecretKey,
} from "../../../shared/reducer";
import { logger, notify } from "../../../shared/utils";
import AuthPartnersDialog from "./AuthPartnersDialog";

const HIDDEN_SECRET = "versteckt";

/** Monospaced key value with a copy control. */
const KeyCell = ({
  value,
  onCopy,
  copyable = true,
}: {
  value: string;
  onCopy: () => void;
  copyable?: boolean;
}) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
    <Typography
      sx={{
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
        fontSize: "0.75rem",
        color: "text.secondary",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        maxWidth: 200,
      }}
      title={value}
    >
      {value}
    </Typography>
    {copyable && (
      <ActionButton icon={<CopyIcon />} label="Kopieren" size="sm" onClick={onCopy} />
    )}
  </Box>
);

const AuthPartners = () => (
  <AuthPartnerProvider>
    <AuthParnerConsumer />
  </AuthPartnerProvider>
);

const AuthParnerConsumer = () => {
  const { state, dispatch } = useContext(AuthPartnerContext);
  const items = state?.allAuthPartners?.items;

  useEffect(() => {
    dispatch({ type: GET_ALL_AUTH_PARTNERS });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [id, setId] = useState<string>("");

  const handleAddKey = () => {
    setId("");
    setDialogOpen(true);
  };

  const handleRecreate = async (partnerId: string) => {
    const confirmed = await isSure(
      "Achtung! Der Private/Public Keys wird nun neu generiert. Sie werden den Private Key nur EINMAL sehen können, solange Sie auf dieser Seite bleiben.\n\nDies kann nicht rückgängig gemacht werden. Sind Sie sicher?",
    );

    if (!confirmed) return;

    dispatch({
      type: RECREATE_AUTH_PARTNER_KEY_PAIR,
      id: partnerId,
    });
  };

  const handleEditKey = (idToEdit: string) => {
    setId(idToEdit);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setId("");
    setDialogOpen(false);
  };

  const handleDeleteKey = async (partnerId: string) => {
    if (!(await isSure())) return;
    dispatch({ type: DELETE_AUTH_PARTNER, id: partnerId });
  };

  const handleCopy = (secretKey: string) => {
    try {
      navigator.clipboard.writeText(secretKey);
    } catch (err) {
      logger("AuthPartners handleCopy", err);
      notify(`${errorMessage}\nUnable to copy key to clipboard.`, "error");
    }
  };

  return (
    <>
      <SettingsSection
        title="API-Partner"
        description="Public/Secret Key-Paare für angebundene Partner"
        columns={[
          "ID",
          "Public Key",
          "Secret Key",
          "Gültig",
          "Erstellt am",
          "Zuletzt verwendet",
          "Geändert",
        ]}
        onAdd={handleAddKey}
        addLabel="Key"
        isEmpty={!items?.length}
        emptyMessage="Es wurden keine Public Keys gefunden."
      >
        {items
          ?.slice()
          // Sort, with next to expire first
          ?.sort(
            (a: AuthPartner, b: AuthPartner) =>
              Number(new Date(a.keyCreatedAt)) -
              Number(new Date(b.keyCreatedAt)),
          )
          ?.map(
            ({
              id: partnerId,
              publicKey,
              secretKey = HIDDEN_SECRET,
              valid,
              keyCreatedAt,
              keyLastUsedAt,
              modified,
            }: TAuthPartnerWithSecretKey) => (
              <TableRow key={partnerId}>
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Box component="span" sx={{ fontWeight: 600 }}>
                      {partnerId}
                    </Box>
                    <ActionButton
                      icon={<CachedIcon />}
                      label="Key-Paar neu generieren"
                      size="sm"
                      onClick={() => handleRecreate(partnerId)}
                    />
                  </Box>
                </TableCell>
                <TableCell>
                  <KeyCell value={publicKey} onCopy={() => handleCopy(publicKey)} />
                </TableCell>
                <TableCell>
                  <KeyCell
                    value={secretKey}
                    copyable={secretKey !== HIDDEN_SECRET}
                    onCopy={() => handleCopy(secretKey)}
                  />
                </TableCell>
                <TableCell>
                  <StatusBadge
                    status={valid ? "Ja" : "Nein"}
                    variant={valid ? "success" : "error"}
                  />
                </TableCell>
                <TableCell sx={{ color: "text.secondary", whiteSpace: "nowrap" }}>
                  <FormattedDateTimeTooltip date={keyCreatedAt} />
                </TableCell>
                <TableCell sx={{ color: "text.secondary", whiteSpace: "nowrap" }}>
                  <FormattedDateTimeTooltip date={keyLastUsedAt} />
                </TableCell>
                <TableCell sx={{ color: "text.secondary", whiteSpace: "nowrap" }}>
                  <FormattedDateTimeTooltip date={modified} />
                </TableCell>
                <TableCell align="right">
                  <TableActions>
                    <ActionButton
                      icon={<EditIcon />}
                      label="Key bearbeiten"
                      onClick={() => handleEditKey(partnerId)}
                    />
                    <ActionButton
                      icon={<DeleteIcon />}
                      label="Key löschen"
                      onClick={() => handleDeleteKey(partnerId)}
                    />
                  </TableActions>
                </TableCell>
              </TableRow>
            ),
          )}
      </SettingsSection>

      {dialogOpen && (
        <AuthPartnersDialog handleCloseDialog={handleCloseDialog} id={id} />
      )}
    </>
  );
};

export default AuthPartners;
